import { useEffect, useState } from "react";

/* How long a connect step waits with no traffic before it says so. Sized to the detour the
 * reader has to make before a request can exist at all — calling it stuck sooner than that
 * reports a failure that hasn't happened. */

/** Editing a file they already have open, then re-running or restarting the agent. */
export const EDITOR_WAIT_MS = 60_000;

/** Leaving for a terminal: run something, sign in, come back. Covers the `npx` handoff and
 *  Codex, which pastes a prompt, lets Codex rewrite config.toml, then runs `codex login`. */
export const TERMINAL_WAIT_MS = 180_000;

/** Where a reader whose first call never lands goes for help (OPSS-333); no docs yet. */
export const SUPPORT_EMAIL = "ai21-gateway-support@ai21.com";

/** True once `delayMs` has passed with no traffic. Traffic outranks a spent budget: a slow
 *  setup still succeeded, and `used` never goes back to false.
 *
 *  `armed` starts the budget. A step that holds its waiting box back until the reader has
 *  worked through the instructions must arm it at that point, not on mount — otherwise a
 *  slow reader is told the setup failed before they had anything to fail at. */
export function useStalled(
  used: boolean,
  delayMs: number,
  armed = true,
): boolean {
  const [expired, setExpired] = useState(false);

  useEffect(() => {
    if (used || !armed) return;

    const id = setTimeout(() => setExpired(true), delayMs);
    return () => clearTimeout(id);
  }, [used, delayMs, armed]);

  return expired && !used;
}
